/**
 * PartnerFinance — деньги партнёра. Шаг Flow (step="partner_finance").
 *
 * Баланс, история начислений и выплат, запрос выплаты. Четыре способа: перевод на свой
 * рублёвый счёт (исполняется сразу), крипто-кошелёк, зарубежная карта и SWIFT.
 *
 * Гейт верификации приходит с бэка (verification): крипта — после проверки личности,
 * карта и SWIFT — после полной верификации с брокером. Перевод на свой счёт доступен всегда.
 * Пропсы: me — состояние партнёра; onBack; onReload — перечитать баланс после операции.
 */
import { useCallback, useEffect, useState } from "react";
import { Screen, Card, Row } from "@/components/Screen";
import { BackLink } from "@/components/BackLink";
import { Button } from "@/components/Button";
import { Field } from "@/components/Field";
import { MaxButton } from "@/components/MaxButton";
import { Note } from "@/components/Note";
import { ErrorText } from "@/components/ErrorText";
import {
  friendlyError,
  partnerApi,
  type CreatePayoutIn,
  type PartnerAccrual,
  type PartnerMe,
  type PartnerPayout,
  type PartnerPayoutMethod,
} from "@/lib/api";

const METHOD_LABEL: Record<PartnerPayoutMethod, string> = {
  internal: "На свой счёт",
  crypto: "Крипто-кошелёк",
  card: "Зарубежная карта",
  swift: "SWIFT-перевод",
};

const ACCRUAL_LABEL: Record<PartnerAccrual["status"], string> = {
  pending: "Ожидает сделки",
  hold: "В холде",
  available: "Доступно",
  cancelled: "Отменено",
};

const PAYOUT_LABEL: Record<PartnerPayout["status"], string> = {
  pending: "Ожидает",
  completed: "Исполнена",
  rejected: "Отклонена",
  cancelled: "Отменена",
};

type Props = {
  me: Extract<PartnerMe, { is_partner: true }>;
  onBack: () => void;
  onReload: () => void;
};

export function PartnerFinance({ me, onBack, onReload }: Props) {
  const [tab, setTab] = useState<"payout" | "history">("payout");
  const [accruals, setAccruals] = useState<PartnerAccrual[]>([]);
  const [payouts, setPayouts] = useState<PartnerPayout[]>([]);

  const reloadHistory = useCallback(() => {
    partnerApi.accruals().then((r) => setAccruals(r.accruals)).catch(() => setAccruals([]));
    partnerApi.payouts().then((r) => setPayouts(r.payouts)).catch(() => setPayouts([]));
  }, []);

  useEffect(reloadHistory, [reloadHistory]);

  return (
    <Screen title="Финансы">
      <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>

      <Card>
        <div className="text-[13px] text-white/45">Доступно</div>
        <div className="mt-1 text-[30px] font-semibold leading-none">
          {me.balance.available_usd.toFixed(2)} <span className="text-[18px] text-white/50">$</span>
        </div>
        <div className="mt-3 border-t border-white/[0.06] pt-2">
          <Row label="В холде" value={`${me.balance.hold_usd.toFixed(2)} $`} />
          <Row label="Заработано всего" value={`${me.balance.total_earned_usd.toFixed(2)} $`} />
        </div>
      </Card>

      <div className="mt-4 flex gap-2">
        <TabButton active={tab === "payout"} onClick={() => setTab("payout")}>Вывести</TabButton>
        <TabButton active={tab === "history"} onClick={() => setTab("history")}>История</TabButton>
      </div>

      {tab === "payout" ? (
        <PayoutForm
          me={me}
          onDone={() => {
            onReload();
            reloadHistory();
            setTab("history");
          }}
        />
      ) : (
        <History accruals={accruals} payouts={payouts} />
      )}
    </Screen>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-xl px-4 py-2.5 text-[14px] font-medium transition ${
        active ? "bg-brand text-surface-base" : "border border-white/10 text-white/60"
      }`}
    >
      {children}
    </button>
  );
}

function PayoutForm({
  me,
  onDone,
}: {
  me: Extract<PartnerMe, { is_partner: true }>;
  onDone: () => void;
}) {
  const [method, setMethod] = useState<PartnerPayoutMethod>("internal");
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [swift, setSwift] = useState({
    beneficiary_name: "",
    iban: "",
    swift: "",
    bank_name: "",
    bank_country: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const v = me.verification;
  const allowed: PartnerPayoutMethod[] = ["internal"];
  if (v.can_payout_crypto) allowed.push("crypto");
  if (v.can_payout_fiat) allowed.push("card", "swift");

  const value = Number(amount.replace(",", "."));
  const min = method === "internal" ? 0 : me.program.min_payout_usd;
  const fee = me.program.payout_fees_usd?.[method] ?? 0;
  const enough = Number.isFinite(value) && value > 0 && value <= me.balance.available_usd && value >= min;

  const submit = async () => {
    if (!enough || busy) return;
    setBusy(true);
    setError("");
    const body: CreatePayoutIn = { method, amount_usd: value };
    if (method === "crypto") body.crypto = { wallet_address: wallet.trim() };
    if (method === "card") {
      body.card = { card_number: cardNumber, first_name: firstName, last_name: lastName };
    }
    if (method === "swift") body.swift = swift;
    try {
      await partnerApi.createPayout(body);
      setAmount("");
      onDone();
    } catch (e) {
      setError(friendlyError(e, "Не удалось создать заявку на выплату."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 gap-2">
        {allowed.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMethod(m)}
            className={`rounded-2xl border p-3 text-left text-[14px] transition ${
              method === m ? "border-brand/60 bg-brand/10" : "border-white/[0.06] bg-surface-card text-white/70"
            }`}
          >
            {METHOD_LABEL[m]}
          </button>
        ))}
      </div>

      {!v.can_payout_crypto && (
        <Note variant="warning" className="mt-3">
          Чтобы выводить вознаграждение наружу, пройдите проверку личности. Перевод на свой
          внутренний счёт доступен без неё.
        </Note>
      )}
      {v.can_payout_crypto && !v.can_payout_fiat && (
        <Note className="mt-3">
          Вывод в рублях и валюте откроется после полной верификации с брокером. Криптовалюты и
          перевод на свой внутренний счёт доступны уже сейчас.
        </Note>
      )}

      <div className="mt-3 space-y-3">
        <Field
          label="Сумма, $"
          type="number"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          suffix={
            <MaxButton
              onClick={() => setAmount(String(Math.floor(me.balance.available_usd * 100) / 100))}
              disabled={me.balance.available_usd <= 0}
            />
          }
          hint={
            method === "internal"
              ? "Зачислим на ваш рублёвый счёт по базовому курсу, без наценки"
              : `Минимум ${me.program.min_payout_usd} $${fee ? `, комиссия ${fee} $` : ""}`
          }
        />

        {method === "crypto" && (
          <Field
            label="Кошелёк TRC20"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            placeholder="T..."
          />
        )}

        {method === "card" && (
          <>
            <Field label="Номер карты" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} />
            <Field label="Имя (латиницей)" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <Field label="Фамилия (латиницей)" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </>
        )}

        {method === "swift" && (
          <>
            <Field
              label="Получатель (латиницей)"
              value={swift.beneficiary_name}
              onChange={(e) => setSwift((s) => ({ ...s, beneficiary_name: e.target.value }))}
            />
            <Field
              label="IBAN или номер счёта"
              value={swift.iban}
              onChange={(e) => setSwift((s) => ({ ...s, iban: e.target.value }))}
            />
            <Field
              label="SWIFT / BIC"
              value={swift.swift}
              onChange={(e) => setSwift((s) => ({ ...s, swift: e.target.value }))}
            />
            <Field
              label="Банк"
              value={swift.bank_name}
              onChange={(e) => setSwift((s) => ({ ...s, bank_name: e.target.value }))}
            />
            <Field
              label="Страна банка"
              value={swift.bank_country}
              onChange={(e) => setSwift((s) => ({ ...s, bank_country: e.target.value }))}
            />
          </>
        )}
      </div>

      <ErrorText className="mt-2">{error}</ErrorText>

      <Button className="mt-4" disabled={!enough || busy} onClick={submit}>
        {busy ? "Отправляем…" : method === "internal" ? "Перевести на счёт" : "Запросить выплату"}
      </Button>
    </div>
  );
}

function History({ accruals, payouts }: { accruals: PartnerAccrual[]; payouts: PartnerPayout[] }) {
  return (
    <div className="mt-4">
      {payouts.length > 0 && (
        <>
          <h2 className="mb-2 text-[15px] font-semibold">Выплаты</h2>
          {payouts.map((p) => (
            <Card key={p.id} className="mb-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-medium">{METHOD_LABEL[p.method]}</span>
                <span className="text-[15px] font-semibold">{p.net_usd.toFixed(2)} $</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[12px] text-white/40">
                <span>{p.reference} · {PAYOUT_LABEL[p.status]}</span>
                <span>{p.created_at ? new Date(p.created_at).toLocaleDateString("ru-RU") : ""}</span>
              </div>
              {p.method === "internal" && p.amount_rub !== null && (
                <div className="mt-1 text-[12px] text-white/40">
                  Зачислено {p.amount_rub.toLocaleString("ru-RU")} ₽ по курсу {p.rate?.toFixed(2)}
                </div>
              )}
              {p.reject_reason && <div className="mt-1 text-[12px] text-red-400">{p.reject_reason}</div>}
            </Card>
          ))}
        </>
      )}

      <h2 className="mb-2 mt-4 text-[15px] font-semibold">Начисления</h2>
      {accruals.length === 0 && <Note>Начислений пока нет.</Note>}
      {accruals.map((a) => (
        <Card key={a.id} className="mb-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-white/70">
              {a.level === "L2" ? "Второй уровень" : "Сделка клиента"}
            </span>
            <span className="text-[15px] font-semibold">{a.amount_usd.toFixed(2)} $</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-[12px] text-white/40">
            <span>
              {ACCRUAL_LABEL[a.status]}
              {a.status === "hold" && a.available_at
                ? ` до ${new Date(a.available_at).toLocaleDateString("ru-RU")}`
                : ""}
            </span>
            <span>{a.turnover_rub.toLocaleString("ru-RU")} ₽</span>
          </div>
        </Card>
      ))}
    </div>
  );
}
